import React, { useEffect, useState } from 'react';
import { Layout, theme, notification, Popover } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import { GapTabs } from 'store/ducks/Gaps/types';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import { gapsOp } from 'store/ducks/Gaps';
import { OptionType } from 'store/ducks/Global/types';
import ApiService from 'utils/ApiCalls';
import gapsRequests from 'constants/gapsRequests';
import Helpers from 'utils/Helpers';
import i18n from 'constants/translation/i18n';
import { useTranslation } from 'react-i18next';
import TitleBar from 'components/TitleBar';
import GoBackButton from 'components/GoBackButton';
import { AuditFindingsLayoutProps } from 'store/ducks/Management/types';
import { globalOp } from 'store/ducks/Global';
import * as Styled from 'components/Layouts/Layout.styled';

const { Content, Sider } = Layout;
const { useToken } = theme;

const AuditFindingsLayout: React.FC<AuditFindingsLayoutProps> = ({
  activeTab,
  activeKey,
  children
}) => {
  const dispatch = useDispatch();
  const { token } = useToken();
  const { t } = useTranslation();

  const [moduleOptionsState, setModuleOptionsState] = useState<OptionType[]>(
    []
  );
  const [groupOptions, setGroupOptions] = useState<
    { label: string; value: number }[]
  >([]);

  const { lang, scheme, drawTable, orgContext } = useSelector(
    (state: RootState) => ({
      lang: state.global.lang,
      scheme: state.global.scheme,
      drawTable: state.complianceEst.drawTable,
      orgContext: state.global.hierarchicalLevelsScope
    })
  );

  const selectedGroupValues = orgContext?.group ?? [];
  const industry = orgContext?.industryType || [];

  const industryArray = Array.isArray(industry)
    ? industry
    : industry
    ? [industry]
    : [];

  const safeModuleArray = Array.isArray(orgContext.module)
    ? orgContext.module
    : orgContext.module !== undefined && orgContext.module !== null
    ? [orgContext.module]
    : [];

  const selectedModuleLabels = safeModuleArray
    .map((m: any) => {
      if (typeof m === 'object' && m !== null) {
        return m.label;
      }

      const option = moduleOptionsState.find((o) => o.value === m);
      return option?.label ?? null;
    })
    .filter(Boolean);

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang]);

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const response = await ApiService.getGroupBPR();

        const data = response.data;
        const groups = Array.isArray(data)
          ? data
          : data.groups ?? data.data ?? [];

        setGroupOptions(
          groups.map((item: any) => ({
            label: item.name ?? item.group_name ?? item.label,
            value: item.id ?? item.group_id ?? item.value
          }))
        );
      } catch (error) {
        console.error('Failed to fetch groups:', error);
      }
    };

    fetchGroups();
  }, []);

  const applyFilters = async () => {
    if (!lang) return;
    const baseParams = Helpers.toApiFilters(orgContext);
    const filterParams = {
      ...baseParams,
      module: selectedModuleLabels
    };

    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;

    if (!Object.keys(filterParams).length) {
      notification.error({
        message: 'Filters Required',
        description: 'Please fill in the filters for displaying',
        duration: 10
      });
      return;
    }

    dispatch(complianceEstOp.setDrawTable(false));
    dispatch(gapsOp.setGapsDescription([]));
    dispatch(gapsOp.setGapsTableData([]));

    const gapsReqs = gapsRequests(dispatch, lang, filterParams);

    dispatch(gapsOp.setAssignedAuditFindingsUpdated(false));
    gapsReqs[GapTabs.AUDIT_FINDINGS]();

    dispatch(complianceEstOp.setDrawTable(true));

    localStorage.setItem('orgContext', JSON.stringify(orgContext));
    // localStorage.setItem('orgContext', JSON.stringify(normalized));
    // dispatch(complianceEstOp.setSaveClicked(true));
  };

  /* ---------------- Init ---------------- */

  useEffect(() => {
    dispatch(complianceEstOp.setFilters(lang));
  }, [lang]);

  const isReadyToApplyFilters =
    !!lang &&
    !!orgContext &&
    Object.keys(orgContext).length > 0 &&
    !!industryArray?.length;

  /* ---------------- Auto-apply filters on mount ---------------- */

  useEffect(() => {
    if (!isReadyToApplyFilters) return;
    applyFilters();
  }, [isReadyToApplyFilters]);

  useEffect(() => {
    dispatch(complianceEstOp.setDrawTable(false));
  }, [activeTab, activeKey]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const moduleGroups = Array.isArray(orgContext.moduleGroup)
          ? orgContext.moduleGroup
          : orgContext.moduleGroup
          ? [orgContext.moduleGroup]
          : [];

        if (!moduleGroups.length) {
          setModuleOptionsState([]);
          return;
        }

        const ids = moduleGroups.map((item: any) =>
          typeof item === 'object' ? item.value : item
        );

        const res = await ApiService.getModulesByModuleGroups({
          module_group_id: ids
        });

        const modules = res?.data?.data ?? [];

        const unique = Array.from(
          new Map(
            modules
              .filter((m: any) => m?.mod_id != null)
              .map((m: any) => [m.mod_id, m])
          ).values()
        );

        setModuleOptionsState(
          unique.map((m: any) => ({
            value: m.mod_id,
            label: m.mod_short_name
          }))
        );
      } catch (err) {
        console.error('Failed to fetch modules:', err);
        setModuleOptionsState([]);
      }
    };

    fetchModules();
  }, [JSON.stringify(orgContext.moduleGroup)]);

  const selectedModuleValues = Array.isArray(orgContext.module)
    ? orgContext.module.map((m: any) => m.value ?? m)
    : [];

  return (
    <Styled.StyledLayout token={token}>
      <TitleBar
        title={i18n.t('AUDIT_FINDINGS_MAPPING')}
        goBackButtons={
          <>
            <GoBackButton
              to='/workingAreaDetails/audit-findings'
              text={t('GO_BACK')}
            />
            <GoBackButton to='/working_area' text={t('GO_TO_WORKING_AREAS')} />
          </>
        }
      />
      <Styled.HorizontalFilterBar token={token} scheme={scheme}>
        <Styled.FiltersContainer>
          <Styled.FiltersSubtitle>
            {t('SELECTED_FOCUS_AREAS', {
              defaultValue: 'Selected Focus Areas'
            })}
          </Styled.FiltersSubtitle>
          <Styled.FilterTextItem>
            <Styled.FilterLabel>{t('MODULE')}</Styled.FilterLabel>
            <Styled.FilterValue>
              {(Array.isArray(selectedModuleValues)
                ? selectedModuleValues
                : [selectedModuleValues]
              )
                .filter((value) => value !== '' && value != null)
                .map(
                  (value) =>
                    moduleOptionsState.find((option) => option.value === value)
                      ?.label ?? value
                )
                .join(', ') || '-'}
            </Styled.FilterValue>
          </Styled.FilterTextItem>

          <Styled.FilterTextItem>
            <Styled.FilterLabel>{t('GROUP')}</Styled.FilterLabel>
            <Styled.FilterValue>
              {(Array.isArray(selectedGroupValues)
                ? selectedGroupValues
                : [selectedGroupValues]
              )
                .filter((value) => value !== '' && value != null)
                .map(
                  (value) =>
                    groupOptions.find((option) => option.value === value)
                      ?.label ?? value
                )
                .join(', ') || '-'}
            </Styled.FilterValue>
          </Styled.FilterTextItem>
        </Styled.FiltersContainer>
        <Styled.ActionButtons
          isDataManagementPage={true}
          hasDisplayedData={drawTable}
        >
          <Styled.ApplyButton
            type='primary'
            onClick={applyFilters}
            scheme={scheme}
          >
            {t('DISPLAY')}
          </Styled.ApplyButton>
        </Styled.ActionButtons>
      </Styled.HorizontalFilterBar>
      <Styled.StyledLayout token={token}>
        <Layout>
          <Content>{children}</Content>
        </Layout>
        <Sider />
      </Styled.StyledLayout>
    </Styled.StyledLayout>
  );
};

export default AuditFindingsLayout;
